'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import Image from '@eliadmin/core/src/components/image/image-wrapped';
import React from 'react';

import type { TDynamicData } from '../../types/dynamic-data';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Heading } from '../ui/heading';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@eliadmin/core/src/components/ui/tabs';
import { useCarouselContext } from '@eliadmin/core/src/components/ui/carousel-context';
import { Carousel } from '../ui/carousel';
import { HtmlRendererRaw } from '@eliadmin/core/src/components/ui/html-renderer-raw';

export function AboutMembers({
  categories,
  members,
  subtitle,
  title,
  description,
}: {
  categories: string[];
  members: TDynamicData[];
  subtitle: string;
  title: string;
  description: string;
}) {
  const [activeTab, setActiveTab] = React.useState<string>(categories[0] || '');

  const filteredMembers = members.filter(member => {
    const categoryField = (member.fields ?? []).find(f => f.field.name === 'Kategória');
    return categoryField?.values?.some(v => v.name === activeTab);
  });

  const carouselContext = useCarouselContext();

  if (!carouselContext) {
    throw new Error('Hero must be used within a CarouselProvider');
  }

  const { selectedIndex, slideCount } = carouselContext;

  const progress = ((selectedIndex + 1) / slideCount) * 100;

  const options = {
    align: 'start' as const,
  };

  return (
    <section
      className={cn(
        'custom-section',
        'gap-8 pb-16 pr-0 pt-6 sm:gap-10 sm:pb-24 sm:pt-12 md:pb-32 md:pr-4 lg:gap-12 lg:pb-36 lg:pt-16 xl:pt-[72px]',
      )}
    >
      <div className={cn('custom-container', 'flex flex-col items-center justify-center gap-4')}>
        <Heading
          subtitle={subtitle}
          title={title}
          className={{
            container: 'max-w-[600px] items-center justify-center pr-4 text-center md:pr-0',
            title: cn('text-balance', 'ea-primary-heading'),
          }}
        />
        <HtmlRendererRaw
          as="p"
          className={cn('pr-4 text-center text-sm text-muted-foreground sm:text-base md:pr-0')}
          html={description}
        />
      </div>
      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className={cn(
          'custom-container',
          'flex flex-col items-center justify-center gap-8 sm:gap-10 lg:gap-12',
        )}
      >
        <TabsList className="overflow-x-none grid h-fit w-full grid-cols-2 items-center justify-center gap-2 rounded-none bg-transparent p-0 pr-4 shadow-none sm:flex sm:gap-3 md:pr-0 lg:gap-4">
          {categories.map(category => (
            <TabsTrigger
              key={category}
              value={category}
              className={cn('ea-references-list__tabs-trigger')}
            >
              {category}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent className={cn('ea-about-members__tabs-content', 'w-full')} value={activeTab}>
          <div className="hidden w-full flex-col items-start justify-start gap-6 md:flex">
            {filteredMembers.map(member => (
              <AboutMemberCard member={member} key={member.id} />
            ))}
          </div>
          <div className="block w-full md:hidden">
            <Carousel
              options={options}
              className={{
                container: 'w-full gap-4 sm:gap-5',
                wrapper: 'w-full',
                main: 'w-full',
                slide: 'w-full max-w-[270px] sm:max-w-[380px]',
              }}
              slides={filteredMembers.map(member => (
                <AboutMemberCard member={member} key={member.id} />
              ))}
            />
          </div>
          <div className="mt-8 block w-full pr-4 sm:mt-10 md:hidden">
            <div className="flex h-[2px] w-full items-center justify-start bg-black/10">
              <div
                className={cn(
                  'h-1 bg-black transition-all duration-300',
                  'ea-team-members__carousel-progress-bar-indicator',
                )}
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </section>
  );
}

function AboutMemberCard({ member }: { member: TDynamicData }) {
  const memberFields = member.fields ?? [];

  return (
    <div
      className={cn(
        'ea-about-member-card',
        'flex h-full w-full flex-col items-start justify-start gap-4 p-4 sm:gap-6 sm:p-6 md:h-fit md:flex-row md:items-center lg:gap-8 lg:p-8',
      )}
    >
      <Image
        src={member.image?.url || '/images/placeholder.png'}
        alt={resolveImageAlt(member?.image, member?.name)}
        width={0}
        height={0}
        className="aspect-square w-full object-cover object-center md:min-w-[200px] md:max-w-[200px]"
        sizes="100vw"
      />
      <div className="flex w-full flex-col items-start justify-start gap-4">
        <div className="flex w-full flex-col items-start justify-start gap-1">
          <h3 data-ea-visual-tag="h5" className="text-lg font-semibold sm:text-xl lg:text-2xl">{member.name}</h3>
          <p className="text-sm font-semibold text-muted-foreground sm:text-base">
            {memberFields[1]?.value}
          </p>
        </div>
        <HtmlRendererRaw
          className={cn('w-full max-w-[600px]', 'ea-about-member__description')}
          html={memberFields[2]?.value || ''}
        />
      </div>
    </div>
  );
}
